/**
 * Camera Automation Behavior
 *
 * Automatically moves the camera to show nodes created/modified off-screen.
 * Uses spring physics for natural-feeling animation. Any user input during
 * animation immediately cancels it (camera stays at current position).
 *
 * Suppressed when the user is actively interacting (drag, resize, edit, etc.).
 */

import type { ViewState } from '../types';
import {
	type SpringConfig,
	type Spring3State,
	DEFAULT_SPRING_CONFIG,
	createSpring3State,
	stepSpring3,
	isSpring3Settled,
	getSpring3Values,
} from '../spring';

// =============================================================================
// Types
// =============================================================================

export interface CameraAutomationConfig {
	/** Spring physics configuration */
	spring: SpringConfig;
	/** Maximum zoom-out level for auto-camera (default: 0.3) */
	minAutoZoom: number;
	/** Viewport margin when fitting nodes, in pixels (default: 80) */
	fitPadding: number;
	/** Try to keep current viewport content visible by zooming out (default: true) */
	preserveViewport: boolean;
	/**
	 * Maximum zoom-out factor relative to current zoom (default: 0.5).
	 * If preserving viewport would require zooming below currentZoom * maxZoomOutFactor,
	 * content is considered "far away" and camera flies directly to the new content.
	 */
	maxZoomOutFactor: number;
}

/** Camera state (minimal, avoids cross-package type dependency) */
interface Camera {
	x: number;
	y: number;
	zoom: number;
}

/** Minimal node shape needed for camera target computation */
interface NodeBounds {
	id: string;
	position: { x: number; y: number };
	size: { width: number; height: number };
}

/** Viewport dimensions */
interface ViewportSize {
	width: number;
	height: number;
}

/** World-space rectangle */
interface WorldRect {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface CameraAutomationDeps {
	/** Get current camera state */
	getCamera(): Camera;
	/** Set camera state */
	setCamera(camera: Camera): void;
	/** Get viewport size */
	getViewport(): ViewportSize;
	/** Get all nodes */
	getNodes(): NodeBounds[];
	/** Get the container element for input listeners */
	getContainer(): HTMLElement;
	/** Get current view state for suppression checks */
	getViewState(): ViewState;
	/** Compute camera to fit nodes */
	fitToNodes(
		nodes: Array<{ position: { x: number; y: number }; size: { width: number; height: number } }>,
		viewport: ViewportSize,
		padding: number,
		minZoom: number,
		maxZoom: number,
	): Camera;
	/** Get the visible world rectangle */
	getVisibleWorldRect(camera: Camera, viewport: ViewportSize): WorldRect;
	/** Optional callback when camera changes */
	onCameraChange?: (camera: Camera) => void;
	/** Optional log function */
	log?: (type: string, detail: string) => void;
}

export type CameraAutomationState = 'idle' | 'animating' | 'suppressed';

export interface CameraAutomation {
	/** Fly the camera to show the given nodes. Suppressed if user is interacting. */
	flyTo(nodeIds: string[]): void;
	/** Instantly jump the camera to show the given nodes (no animation). Not suppressed. */
	jumpTo(nodeIds: string[]): void;
	/** Cancel any in-progress animation. Camera stays at current position. */
	cancel(): void;
	/** Whether a spring animation is currently running. */
	isAnimating(): boolean;
	/** Get current state: idle, animating, or suppressed. */
	getState(): CameraAutomationState;
	/** Get the suppression reason, if any. */
	getSuppressionReason(): string | null;
	/** Update config at runtime. */
	setConfig(config: Partial<CameraAutomationConfig>): void;
	/** Get current config. */
	getConfig(): CameraAutomationConfig;
	/** Clean up resources. */
	destroy(): void;
}

// =============================================================================
// Defaults
// =============================================================================

const DEFAULT_CONFIG: CameraAutomationConfig = {
	spring: { ...DEFAULT_SPRING_CONFIG },
	minAutoZoom: 0.3,
	fitPadding: 80,
	preserveViewport: true,
	maxZoomOutFactor: 0.5,
};

// =============================================================================
// Implementation
// =============================================================================

export function createCameraAutomation(deps: CameraAutomationDeps): CameraAutomation {
	const { getCamera, setCamera, getViewport, getNodes, getContainer, getViewState, fitToNodes, getVisibleWorldRect, onCameraChange, log } = deps;

	let config: CameraAutomationConfig = { ...DEFAULT_CONFIG, spring: { ...DEFAULT_CONFIG.spring } };
	let rafId: number | null = null;
	let springState: Spring3State | null = null;
	let springTarget: Camera | null = null;
	let lastTime: number | null = null;

	// =========================================================================
	// Suppression checks
	// =========================================================================

	function checkSuppression(): string | null {
		const vs = getViewState();
		if (vs.drag) return 'drag';
		if (vs.resize) return 'resize';
		if (vs.editing) return 'editing';
		if (vs.boxSelect) return 'box-select';
		if (vs.connecting) return 'connecting';
		return null;
	}

	// =========================================================================
	// User input cancellation
	// =========================================================================

	const cancelEvents = ['mousedown', 'keydown', 'wheel'] as const;
	const cancelListeners: Array<() => void> = [];

	function startListeningForCancel(): void {
		const container = getContainer();
		for (const eventType of cancelEvents) {
			const handler = () => {
				if (rafId !== null) {
					cancelAnimation();
					log?.('camera-auto-cancel', `user ${eventType} during animation`);
				}
			};
			container.addEventListener(eventType, handler, { capture: true, passive: true });
			cancelListeners.push(() => container.removeEventListener(eventType, handler, { capture: true } as EventListenerOptions));
		}
	}

	function stopListeningForCancel(): void {
		for (const cleanup of cancelListeners) {
			cleanup();
		}
		cancelListeners.length = 0;
	}

	// =========================================================================
	// Animation loop
	// =========================================================================

	function animate(): void {
		if (!springState || !springTarget) {
			stopAnimation();
			return;
		}

		const now = Date.now();
		if (lastTime === null) {
			lastTime = now;
		}

		// Compute dt in seconds, capped to prevent large jumps
		const dt = Math.min((now - lastTime) / 1000, 1 / 30);
		lastTime = now;

		// Step the spring
		springState = stepSpring3(springState, springTarget, config.spring, dt);

		// Apply to camera
		const values = getSpring3Values(springState);
		const cam = { x: values.x, y: values.y, zoom: values.zoom };
		setCamera(cam);
		onCameraChange?.(cam);

		// Check if settled
		if (isSpring3Settled(springState, springTarget, config.spring)) {
			// Snap to exact target
			const finalTarget = springTarget;
			setCamera(finalTarget);
			onCameraChange?.(finalTarget);
			stopAnimation();
			log?.('camera-auto-complete', `settled at (${finalTarget.x.toFixed(0)}, ${finalTarget.y.toFixed(0)}) z${finalTarget.zoom.toFixed(2)}`);
			return;
		}

		rafId = requestAnimationFrame(animate);
	}

	function startAnimation(target: Camera): void {
		// Initialize spring from current camera
		const current = getCamera();
		springState = createSpring3State(current);
		springTarget = target;
		lastTime = null;

		startListeningForCancel();
		rafId = requestAnimationFrame(animate);
	}

	function stopAnimation(): void {
		if (rafId !== null) {
			cancelAnimationFrame(rafId);
			rafId = null;
		}
		springState = null;
		springTarget = null;
		lastTime = null;
		stopListeningForCancel();
	}

	function cancelAnimation(): void {
		// Camera stays at its current interpolated position — no snap-back
		stopAnimation();
	}

	// =========================================================================
	// Target computation (shared by flyTo and jumpTo)
	// =========================================================================

	/**
	 * Compute the target camera to frame the given node IDs.
	 * Returns null if nodes are empty, not found, or already visible.
	 */
	function computeTarget(nodeIds: string[]): Camera | null {
		if (nodeIds.length === 0) return null;

		const allNodes = getNodes();
		const targetNodes = allNodes.filter(n => nodeIds.includes(n.id));
		if (targetNodes.length === 0) return null;

		const viewport = getViewport();
		const currentCamera = getCamera();

		// Compute camera to fit only target nodes
		const targetCamera = fitToNodes(
			targetNodes.map(n => ({ position: n.position, size: n.size })),
			viewport,
			config.fitPadding,
			config.minAutoZoom,
			currentCamera.zoom, // Don't zoom in beyond current zoom
		);

		// Check if nodes are already visible
		const visibleRect = getVisibleWorldRect(currentCamera, viewport);
		const allVisible = targetNodes.every(n => {
			const nx = n.position.x;
			const ny = n.position.y;
			const nr = nx + n.size.width;
			const nb = ny + n.size.height;
			return (
				nx >= visibleRect.x &&
				ny >= visibleRect.y &&
				nr <= visibleRect.x + visibleRect.width &&
				nb <= visibleRect.y + visibleRect.height
			);
		});

		if (allVisible) {
			log?.('camera-auto-skip', 'nodes already visible');
			return null;
		}

		let result: Camera;

		if (config.preserveViewport) {
			// Try to keep current viewport content visible by zooming out
			// Include the full visible world rect as a "node" in the fit calculation
			const viewportAsNode = {
				position: { x: visibleRect.x, y: visibleRect.y },
				size: { width: visibleRect.width, height: visibleRect.height },
			};
			const combinedCamera = fitToNodes(
				[viewportAsNode, ...targetNodes.map(n => ({ position: n.position, size: n.size }))],
				viewport,
				config.fitPadding,
				config.minAutoZoom,
				currentCamera.zoom,
			);

			const zoomRatio = combinedCamera.zoom / currentCamera.zoom;

			if (zoomRatio < config.maxZoomOutFactor) {
				// Content is really far away — just fly to new content directly
				log?.('camera-auto-far', `zoom ratio ${zoomRatio.toFixed(2)} < threshold ${config.maxZoomOutFactor} — flying directly`);
				result = targetCamera;
			} else {
				// Gentle zoom out to show both current viewport and new content
				result = combinedCamera;
			}
		} else {
			// preserveViewport=false: original behavior
			const zoomRatio = targetCamera.zoom / currentCamera.zoom;

			if (zoomRatio < 0.7) {
				// Significant zoom out needed — fit both current view center and target nodes
				const combinedNodes = [
					{ position: { x: currentCamera.x - 50, y: currentCamera.y - 50 }, size: { width: 100, height: 100 } },
					...targetNodes.map(n => ({ position: n.position, size: n.size })),
				];
				result = fitToNodes(
					combinedNodes,
					viewport,
					config.fitPadding,
					config.minAutoZoom,
					currentCamera.zoom,
				);
			} else {
				// Close enough — just pan (keep current zoom)
				result = { ...targetCamera, zoom: currentCamera.zoom };
			}
		}

		// =====================================================================
		// Gentle zoom-in for tiny nodes
		// =====================================================================
		// If target nodes are very small at the computed zoom, zoom in gently
		// so they're visible without aggressively disrupting the viewport.
		const maxScreenDim = Math.max(
			...targetNodes.map(n => Math.max(n.size.width, n.size.height) * result.zoom)
		);
		if (maxScreenDim < 60) {
			const largestWorldDim = Math.max(
				...targetNodes.map(n => Math.max(n.size.width, n.size.height))
			);
			// Zoom to make largest node ~150px on screen
			const idealZoom = 150 / largestWorldDim;
			// Cap at 1.5× current zoom
			const maxGentleZoom = currentCamera.zoom * 1.5;
			// Don't exceed the uncapped naive fit zoom
			const naiveFitZoom = fitToNodes(
				targetNodes.map(n => ({ position: n.position, size: n.size })),
				viewport,
				config.fitPadding,
				config.minAutoZoom,
				Infinity,
			).zoom;
			const gentleZoom = Math.min(idealZoom, maxGentleZoom, naiveFitZoom);
			if (gentleZoom > result.zoom) {
				log?.('camera-auto-tiny', `nodes tiny (${maxScreenDim.toFixed(0)}px), gentle zoom ${result.zoom.toFixed(2)} → ${gentleZoom.toFixed(2)}`);
				// Re-center on target nodes since we're zooming in
				result = { x: targetCamera.x, y: targetCamera.y, zoom: gentleZoom };
			}
		}

		return result;
	}

	// =========================================================================
	// Public API
	// =========================================================================

	return {
		flyTo(nodeIds: string[]): void {
			// Check suppression
			const reason = checkSuppression();
			if (reason) {
				log?.('camera-auto-suppressed', `reason: ${reason}`);
				return;
			}

			const target = computeTarget(nodeIds);
			if (!target) return;

			// Stop any in-progress animation
			stopAnimation();

			log?.('camera-auto-start', `flying to (${target.x.toFixed(0)}, ${target.y.toFixed(0)}) z${target.zoom.toFixed(2)}`);
			startAnimation(target);
		},

		jumpTo(nodeIds: string[]): void {
			const target = computeTarget(nodeIds);
			if (!target) return;

			// Stop any in-progress animation
			stopAnimation();

			setCamera(target);
			onCameraChange?.(target);
			log?.('camera-auto-jump', `jumped to (${target.x.toFixed(0)}, ${target.y.toFixed(0)}) z${target.zoom.toFixed(2)}`);
		},

		cancel(): void {
			if (rafId !== null) {
				cancelAnimation();
				log?.('camera-auto-cancel', 'manual cancel');
			}
		},

		isAnimating(): boolean {
			return rafId !== null;
		},

		getState(): CameraAutomationState {
			if (rafId !== null) return 'animating';
			const reason = checkSuppression();
			if (reason) return 'suppressed';
			return 'idle';
		},

		getSuppressionReason(): string | null {
			return checkSuppression();
		},

		setConfig(newConfig: Partial<CameraAutomationConfig>): void {
			if (newConfig.spring) {
				config.spring = { ...config.spring, ...newConfig.spring };
			}
			if (newConfig.minAutoZoom !== undefined) {
				config.minAutoZoom = newConfig.minAutoZoom;
			}
			if (newConfig.fitPadding !== undefined) {
				config.fitPadding = newConfig.fitPadding;
			}
			if (newConfig.preserveViewport !== undefined) {
				config.preserveViewport = newConfig.preserveViewport;
			}
			if (newConfig.maxZoomOutFactor !== undefined) {
				config.maxZoomOutFactor = newConfig.maxZoomOutFactor;
			}
		},

		getConfig(): CameraAutomationConfig {
			return { ...config, spring: { ...config.spring } };
		},

		destroy(): void {
			stopAnimation();
		},
	};
}
